import { Panel } from '../ui/Panel';
import { money } from '../../core/format/currency';
import { channelLabel } from '../../core/rules/revenue';
import type { ChannelBreakdown } from '../../core/types/adminRequests';
import type { StockPeriod } from '../../core/types/stock';

/** Omzet per kanaal naast elkaar, met wat erin zit. Samen precies de totale omzet. */
export function ChannelSplit({
  rows,
  totalCents
}: {
  rows: ChannelBreakdown[];
  totalCents: number;
}) {
  return <Panel title="Per kanaal" description="Omzet excl. btw. Samen de totale omzet van alle kanalen." data-id="element-3773" data-mp="12sdmqb5xbeyc">
      <div className="grid gap-6 sm:grid-cols-2" data-id="element-3774" data-mp="xqwkocbpdmg6">
        {rows.map(c => {
        const share = totalCents > 0 ? Math.round(c.revenueExVatCents / totalCents * 100) : 0;
        const parts = [{
          label: 'Producten',
          cents: c.productsExVatCents
        }, {
          label: 'Behandelingen',
          cents: c.treatmentsExVatCents
        }, {
          label: 'Kuren',
          cents: c.kurenExVatCents
        }, {
          label: 'Verzending',
          cents: c.shippingExVatCents
        }].filter(p => p.cents !== 0);
        return <section key={c.channel} aria-labelledby={`kanaal-${c.channel}`} data-id="element-3775" data-mp="3htd6oncfws5o">
              <div className="flex items-baseline justify-between gap-3" data-id="element-3776" data-mp="3hracrs862evo">
                <h3 id={`kanaal-${c.channel}`} className="text-[14px] text-ink" data-id="element-3777" data-mp="3mkmwpp1whxi3">
                  {channelLabel[c.channel]}
                </h3>
                <span className="text-[12px] text-muted" data-id="element-3778" data-mp="2ut70r4egeyf7">
                  {c.sales} {c.channel === 'salon' ? c.sales === 1 ? 'bon' : 'bonnen' : c.sales === 1 ? 'bestelling' : 'bestellingen'}
                </span>
              </div>
              <p className="mt-1 text-[1.6rem] font-light leading-none tabular-nums text-ink" data-id="element-3779" data-mp="33su4hwunogbf">{money(c.revenueExVatCents)}</p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-line" aria-hidden="true" data-id="element-3780" data-mp="38tzzy2fpahon">
                <div className="h-full rounded-full bg-clay" style={{
              width: `${Math.max(0, Math.min(100, share))}%`
            }} data-id="element-3781" data-mp="j34npeae6kg3" />
              </div>
              <p className="mt-1 text-[12px] text-muted" data-id="element-3782" data-mp="3uj6rdoomc019">{share}% van de omzet</p>
              {parts.length === 0 ? <p className="mt-3 text-[13px] text-muted" data-id="element-3783" data-mp="3pjlgqh03638p">Niets in deze periode.</p> : <dl className="mt-3 space-y-1.5 text-[13px]" data-id="element-3784" data-mp="8bgid4opicna">
                  {parts.map(p => <div key={p.label} className="flex justify-between gap-3" data-id="element-3785" data-mp="3eq7cvrre78q">
                      <dt className="text-muted" data-id="element-3786" data-mp="3bsfbe4a4lbdw">{p.label}</dt>
                      <dd className="tabular-nums text-ink" data-id="element-3787" data-mp="3bqvvwfwan4d6">{money(p.cents)}</dd>
                    </div>)}
                </dl>}
            </section>;
      })}
      </div>
    </Panel>;
}
const stockRows: {
  key: keyof StockPeriod;
  label: string;
}[] = [{
  key: 'soldWebshop',
  label: 'Verkocht via de webshop'
}, {
  key: 'soldSalon',
  label: 'Verkocht in de salon'
}, {
  key: 'used',
  label: 'In gebruik genomen'
}, {
  key: 'writtenOff',
  label: 'Afgeschreven'
}];

/** Voorraad deze periode: stuks en waarde tegen inkoopprijs. Zonder inkoopprijs telt een stuk als € 0. */
export function StockPeriodPanel({
  stock,
  onExport,
  exporting
}: {
  stock: StockPeriod;
  onExport: () => void;
  exporting: boolean;
}) {
  return <Panel title="Voorraad deze periode" description="Alle kanalen. Waarde tegen inkoopprijs excl. btw; zonder inkoopprijs telt een stuk als € 0." actions={<button type="button" onClick={onExport} disabled={exporting} className="text-[13px] text-ink underline underline-offset-4 hover:text-clay disabled:opacity-60" data-id="element-3789" data-mp="1mbte3orayoo6">
          Mutaties (CSV)
        </button>} data-id="element-3788" data-mp="tp0dd6dwxpe6">
      <table className="w-full text-[14px]" data-id="element-3790" data-mp="1davav8ooul6n">
        <caption className="sr-only" data-id="element-3791" data-mp="1hcqb09uvusiu">Voorraad deze periode</caption>
        <thead className="text-[12px] text-muted" data-id="element-3792" data-mp="1hb6xgr0m2vkr">
          <tr className="border-b border-line" data-id="element-3793" data-mp="1vzxrr5owrs7">
            <th scope="col" className="py-2 text-left font-normal" data-id="element-3794" data-mp="1ycczwk2co1k">
              <span className="sr-only" data-id="element-3795" data-mp="5ymjdabrpggy">Soort</span>
            </th>
            <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-3796" data-mp="m5hlbijeo1zs">
              Stuks
            </th>
            <th scope="col" className="py-2 text-right font-normal" data-id="element-3797" data-mp="hdzj6r5nbvd8">
              Inkoopwaarde
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line" data-id="element-3798" data-mp="cffw2nfbgdja">
          {stockRows.map(r => <tr key={r.key} data-id="element-3799" data-mp="7ho5pxubkzh7">
              <th scope="row" className="py-2.5 text-left font-normal text-muted" data-id="element-3800" data-mp="7e8q8twqhof9">
                {r.label}
              </th>
              <td className="px-2 py-2.5 text-right tabular-nums text-ink" data-id="element-3801" data-mp="1048xv0imdflt">{stock[r.key].qty}</td>
              <td className="py-2.5 text-right tabular-nums text-ink" data-id="element-3802" data-mp="v54nw7nvfduf">{money(stock[r.key].costCents)}</td>
            </tr>)}
        </tbody>
      </table>
    </Panel>;
}